import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  AccessTime as AttendanceIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AttendanceAnalytics = ({ data, meta }) => {
  if (!data) return null;

  // Attendance Trend Chart
  const trendData = {
    labels: data.monthlyData?.map(item => item.month) || [],
    datasets: [
      {
        label: 'Attendance Rate (%)',
        data: data.monthlyData?.map(item => item.presentPercentage) || [],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#1976d2',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: 'Late Arrivals',
        data: data.monthlyData?.map(item => item.totalLate) || [],
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#f57c00',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        yAxisID: 'y1',
      },
    ],
  };

  // Department-wise Attendance
  const departmentData = {
    labels: data.departmentWise?.map(item => item.department) || [],
    datasets: [
      {
        label: 'Attendance Rate (%)',
        data: data.departmentWise?.map(item => Math.round(item.attendancePercentage)) || [],
        backgroundColor: [
          '#1976d2',
          '#2e7d32',
          '#f57c00',
          '#9c27b0',
          '#d32f2f',
          '#7b1fa2'
        ],
        borderWidth: 1,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Attendance Rate (%)',
        },
        beginAtZero: true,
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Late Arrivals Count',
        },
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Attendance Rate (%)',
        },
      },
    },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Attendance Analytics Overview
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttendanceIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {data.keyMetrics?.avgAttendanceRate || 0}%
                </Typography>
              </Box>
              <Typography variant="body2">Avg Attendance Rate</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttendanceIcon sx={{ color: '#2e7d32', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  {data.currentMonth?.punctualityRate || 0}%
                </Typography>
              </Box>
              <Typography variant="body2">This Month Punctuality</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#f57c00', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                  {data.keyMetrics?.totalLateArrivals || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Late Arrivals</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fce4ec', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttendanceIcon sx={{ color: '#c2185b', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#c2185b', fontWeight: 'bold' }}>
                  {data.keyMetrics?.totalAbsentDays || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Absent Days</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Attendance Trend */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Attendance Trend (Last 12 Months)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 350 }}>
                <Line data={trendData} options={lineOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Department Attendance */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Department-wise Attendance
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 350 }}>
                <Bar
                  data={departmentData}
                  options={{
                    ...barOptions,
                    plugins: {
                      ...barOptions.plugins,
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AttendanceAnalytics;
